import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Callout, Dialog, Form, Toast } from "@sscs/ui";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { REVOKE_FORM_DEFAULT_VALUES, REVOKE_REASONS } from "./constants";
import Reason from "./Reason";
import type { RevokeDialogProps, RevokeFormData } from "./types";

const FormSchema = yup.object({
  reason: yup
    .string()
    .required("Please specify reason for revoking approval")
    .max(500, "Max reason length is 500 characters"),
});

const RevokeDialogContent = ({
  onClose,
  subjectId,
  editApproval,
  warning,
}: Omit<RevokeDialogProps, "isOpen">): React.JSX.Element => {
  const form = useForm<RevokeFormData>({
    resolver: yupResolver(FormSchema),
    defaultValues: REVOKE_FORM_DEFAULT_VALUES,
  });

  const onSubmit: SubmitHandler<RevokeFormData> = async (data) => {
    try {
      await editApproval({
        subjectId,
        body: {
          approval_status: "ApprovalRevoked",
          reason: data.reason,
        },
      }).unwrap();
    } catch (error) {
      apiErrorToast(error, "Could not save approval data");
      return;
    }

    Toast.show({
      title: "Approval revoked",
      type: "success",
    });

    onClose();
  };

  return (
    <FormProvider {...form}>
      <Form.Form onSubmit={form.handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>Revoke approval</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Content>
              {warning && (
                <Callout type="warning" title={warning.title}>
                  {warning.body}
                </Callout>
              )}
              <Reason selection={REVOKE_REASONS} />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={form.formState.isSubmitting}
            >
              Confirm
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const RevokeDialog = ({
  isOpen,
  ...props
}: RevokeDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <RevokeDialogContent {...props} />
  </Dialog.Frame>
);

export default RevokeDialog;
